import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MapPin, Navigation, Loader2, RefreshCw, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reverse geocoding to get address from coordinates
async function reverseGeocode(lat, lng) {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`
    );
    const data = await response.json();
    if (data.display_name) {
      return data.display_name;
    }
  } catch (error) {
    console.error('Geocoding error:', error);
  }
  return null;
}

export function useLocationTracker(options = {}) {
  const { getAuthHeaders, user } = useAuth();
  const [location, setLocation] = useState(null);
  const [address, setAddress] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const intervalRef = useRef(null);
  
  const updateInterval = options.updateInterval || 10000; // 10 seconds default
  const enableAutoTracking = options.enableAutoTracking ?? false;

  const updateLocationOnServer = useCallback(async (lat, lng, addr) => {
    try {
      await axios.put(
        `${API}/user/location`,
        { lat, lng, address: addr },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to update location on server:', error);
    }
  }, [getAuthHeaders]);

  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude, accuracy } = position.coords;
          const addr = await reverseGeocode(latitude, longitude);
          
          setLocation({ lat: latitude, lng: longitude, accuracy });
          setAddress(addr);
          setError(null);
          
          resolve({ lat: latitude, lng: longitude, address: addr, accuracy });
        },
        (error) => {
          setError(error.message);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }, []);

  const updateLocation = useCallback(async (showToast = false) => {
    setLoading(true);
    try {
      const loc = await getCurrentLocation();
      await updateLocationOnServer(loc.lat, loc.lng, loc.address);
      
      if (showToast) {
        toast.success('Location updated!');
      }
      
      return loc;
    } catch (error) {
      if (showToast) {
        toast.error('Failed to get location: ' + error.message);
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [getCurrentLocation, updateLocationOnServer]);

  const startTracking = useCallback(() => {
    if (tracking) return;
    
    setTracking(true);
    
    // Initial update
    updateLocation();
    
    // Set up interval for continuous updates
    intervalRef.current = setInterval(() => {
      updateLocation();
    }, updateInterval);
    
    toast.success('Live location tracking started');
  }, [tracking, updateLocation, updateInterval]);

  const stopTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTracking(false);
    toast.info('Location tracking stopped');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Auto-start tracking if enabled
  useEffect(() => {
    if (enableAutoTracking && user) {
      startTracking();
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enableAutoTracking, user]);

  return {
    location,
    address,
    tracking,
    loading,
    error,
    lastUpdate,
    updateLocation,
    startTracking,
    stopTracking,
    getCurrentLocation
  };
}

export function LocationUpdateButton({ compact = false, showAddress = true }) {
  const { location, address, loading, error, lastUpdate, updateLocation, tracking, startTracking, stopTracking } = useLocationTracker();

  if (compact) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => updateLocation(true)}
        disabled={loading}
        className="gap-2"
        data-testid="update-location-btn"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <MapPin className="w-4 h-4" />
        )}
        {loading ? 'Updating...' : 'Update Location'}
      </Button>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-blue-600" />
          Your Location
        </h4>
        {tracking && (
          <span className="flex items-center gap-1.5 text-xs text-green-600">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Live Tracking
          </span>
        )}
      </div>

      {error && (
        <div className="p-2 bg-red-50 text-red-600 text-sm rounded flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {location && (
        <div className="text-sm text-gray-600">
          <p className="flex items-center gap-1">
            <Check className="w-4 h-4 text-green-500" />
            Lat: {location.lat.toFixed(6)}, Lng: {location.lng.toFixed(6)}
          </p>
          {showAddress && address && (
            <p className="mt-1 text-gray-500 text-xs truncate" title={address}>
              {address}
            </p>
          )}
          {lastUpdate && (
            <p className="mt-1 text-xs text-gray-400">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => updateLocation(true)}
          disabled={loading}
          className="flex-1"
          data-testid="manual-update-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Update Now
        </Button>
        
        {tracking ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={stopTracking}
            className="flex-1"
            data-testid="stop-tracking-btn"
          >
            Stop Tracking
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={startTracking}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="start-tracking-btn"
          >
            <Navigation className="w-4 h-4 mr-2" />
            Live Track
          </Button>
        )}
      </div>
    </div>
  );
}

export function LocationPinUpdater({ onLocationUpdate, requestId }) {
  const { location, address, loading, updateLocation } = useLocationTracker();
  const { getAuthHeaders } = useAuth();
  const [updating, setUpdating] = useState(false);

  const handleUpdatePin = async () => {
    setUpdating(true);
    try {
      const loc = await updateLocation(false);
      
      // Also update the specific service request if provided
      if (requestId) {
        await axios.put(
          `${API}/services/${requestId}/location`,
          { lat: loc.lat, lng: loc.lng, address: loc.address },
          { headers: getAuthHeaders(), withCredentials: true }
        );
      }
      
      toast.success('Location pin updated!');
      if (onLocationUpdate) {
        onLocationUpdate(loc);
      }
    } catch (error) {
      toast.error('Failed to update location');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
      <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">Update Your Pin</p>
        {location && (
          <p className="text-xs text-gray-500 truncate">
            Current: {address || `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}`}
          </p>
        )}
      </div>
      <Button
        size="sm"
        onClick={handleUpdatePin}
        disabled={updating || loading}
        className="bg-blue-600 hover:bg-blue-700"
        data-testid="update-pin-btn"
      >
        {(updating || loading) ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <>
            <RefreshCw className="w-4 h-4 mr-1" />
            Update
          </>
        )}
      </Button>
    </div>
  );
}

export default LocationUpdateButton;
